import { type PlanSpec } from '@/designs-dev/plan/types';

/**
 * Tell Us Once: what it is, and the shape of getting there.
 *
 * One section, on purpose. The project's pages each cover one piece — the
 * patterns, the intake, the CAM side, the calculator, the claim — and this page
 * is only the thing they are pieces of: a definition, and how the stages sit
 * against each other.
 *
 * Nothing here is a schedule. The stages overlap because the work does, and the
 * widths are relative rather than dates. What is in Linear is the commitment.
 */
export const tellUsOncePlan: PlanSpec = {
  id: 'tell-us-once',
  title: 'Tell us once',

  definition:
    'Whatever a carer tells us, out loud, photographed, uploaded or typed, is kept, and no claim asks for it a second time. We fill what we can from what is given, so we don’t have to ask again later down the line. Nobody has to repeat themselves, and they choose how to tell us, whatever suits them at the time.',

  // The two months the work has actually been in, then everything after them
  // in one band: nothing past October is sequenced, and naming further months
  // would imply it was. Four columns each.
  axis: ['August', 'September', 'October onwards'],

  // Twelfths, and they overlap on purpose: the calculator starts before
  // onboarding is finished, and the claim starts before the calculator is.
  stages: [
    {
      title: 'Infrastructure',
      definition:
        'Capture in, clean text out, answers with a confidence on them. None of it user-facing. It goes first because everything after it is only worth designing if a document can be read well enough to trust.',
      footnotes: [
        'Tech constraint: asynchronous. Nothing is read while the carer is still talking, so everything built on these foundations has to work after the fact.',
        'No designs for this stage. It is internal, and nothing user-facing comes out of it.',
      ],
      state: 'done',
      from: 1,
      span: 5,
    },
    {
      title: 'Onboarding',
      definition:
        'The conversation that replaces form onboarding, and the review that follows it. It is the first place a carer tells us anything, so it is where the facts every later claim reuses come from.',
      state: 'nearly',
      from: 3,
      span: 6,
    },
    {
      title: 'Benefits calculator',
      definition:
        'The same conversation, opening on what we already hold and ending on a claim rather than a number. It is how a carer finds out there is anything to claim at all.',
      state: 'building',
      from: 9,
      span: 4,
    },
    {
      title: 'Dynamic flows',
      definition:
        'An agent in the app that works out which route to send someone down as they go. It sits behind any flow, like onboarding or the benefits calculator, so the path is worked out for them rather than fixed.',
      // No bar: its timing has not been set.
      state: 'not-started',
    },
    {
      title: 'Attendance Allowance',
      definition:
        'The first claim built from facts we already have. The real form is 227 questions and most of them are already answered by this point, which is the whole argument for asking once.',
      state: 'unordered',
      // Follows the calculator, which is already being built.
      from: 9,
      span: 4,
    },
    {
      // Its own plan rather than a stage of this one, and it starts early:
      // every stage from onboarding onwards produces a claim somebody has to
      // read and send.
      title: 'CAM insight',
      definition:
        'What the care expert picks up: the facts, where each came from, and what is still missing. Every stage from onboarding onwards produces a claim somebody has to read and send, so it cannot come last.',
      state: 'unordered',
      from: 5,
      span: 8,
    },
    {
      title: 'The claims after it',
      definition:
        "Carer's Allowance, Council Tax reduction, Pension Credit. Each one should be a question set rather than a new design, and the cost of the second claim is the real test of the idea.",
      state: 'unordered',
      from: 10,
      span: 3,
    },
    {
      title: 'Synchronous foundations',
      definition:
        'Reading while the carer is still talking rather than after they have gone. It is the thing that lifts the constraint on the foundations: until it exists, a question we cannot read a free answer to has to take the input away, and every pattern is shaped around that.',
      // No bar: the timing of this has not been discussed, and a dashed box
      // in the last month would be a guess dressed as a plan.
      state: 'unordered',
    },
  ],

  links: [
    {
      label: 'Exploration: how this got here',
      url: '/tell-us-once/exploration',
    },
    { label: 'The patterns', url: '/tell-us-once/flows/patterns' },
    { label: 'The prototype', url: '/tell-us-once/intake-profile-review' },
    { label: 'CAM overview', url: '/tell-us-once/flows/cam-overview' },
    { label: 'Getting in, the project before this', url: '/onboarding' },
  ],
};
